import { byPrefixAndName } from '@awesome.me/kit-5c288c4b3f/icons'
import { Chip } from '@dv01-inc/waterfall-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { graphql, HeadProps, Link, PageProps } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'
import {
  Button,
  Layout,
  ResourceCard,
  ResourceSidebar,
  SEO,
  SubNav,
  ValuePropBullet,
} from '../../../components'
import { resourcesStyles } from '../../../components/resources/resourcesStyles'
import { SeoProps } from '../../../types'
import { cn, richTextOptions } from '../../../utils'

const ResourcesCategory = ({ data }: PageProps<Queries.ResourcesPageQuery>) => {
  return (
    <Layout
      background={
        data.contentfulResources.category.slug === 'research' ? 'light' : 'dark'
      }
    >
      <main className="w-full pt-36" id="main-content">
        <SubNav
          path="resources"
          navItems={data.allContentfulResourceCategories.nodes}
        />
        <div className={resourcesStyles.gridColumns.base}>
          <div className="col-span-12 font-bold md:col-span-10 md:col-start-3">
            <h3 className={resourcesStyles.cardCategory.base}>
              {data.contentfulResources.featured && (
                <span className={resourcesStyles.cardPill.base}>Featured</span>
              )}

              {data.contentfulResources.category.name}
            </h3>
            <h1 className="text-lg md:text-4xl">
              {data.contentfulResources.title}
            </h1>
            <span className={resourcesStyles.cardDate.base}>
              {data.contentfulResources.date}
            </span>
          </div>
        </div>
        <GatsbyImage
          image={data.contentfulResources.heroImage.gatsbyImageData}
          alt={data.contentfulResources.heroImage.title}
          className="mb-12 h-50 md:h-104"
        />
        <div className={cn(resourcesStyles.gridColumns.base)}>
          <ResourceSidebar>
            {data.contentfulResources.tags && (
              <div className="mb-xxl flex flex-col items-start gap-s">
                <h2 className="mb-xxs text-xxs font-bold uppercase text-default-400">
                  Tags
                </h2>
                {data.contentfulResources.tags.map((tag) => (
                  <Link
                    key={tag.id}
                    className="rounded-md bg-default-800 px-s py-xs text-xxxxs leading-none hover:bg-primary hover:text-default-1000"
                    to={`/resources/tag/${tag.slug}/`}
                  >
                    {tag.name}
                  </Link>
                ))}
              </div>
            )}
            <div className="flex flex-col items-start gap-s pt-xxl">
              <h2 className="mb-xxs text-xxs font-bold uppercase text-default-400">
                Share
              </h2>
              <div className="flex gap-2">
                <a
                  id="share"
                  href={`https://linkedin.com/shareArticle?url=https://www.dv01.co/resources/${data.contentfulResources.category.slug}/${data.contentfulResources.slug}/&title=${data.contentfulResources.title}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Chip color="bg-default-800 hover:bg-primary" size="xl">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fab['linkedin-in']}
                      size="xs"
                    />
                  </Chip>
                  <span className="sr-only">Share on LinkedIn</span>
                </a>
                <a
                  id="share"
                  href={`mailto:?body=https://www.dv01.co/resources/${data.contentfulResources.category.slug}/${data.contentfulResources.slug}/`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Chip color="bg-default-800 hover:bg-primary" size="xl">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fas['envelope']}
                      size="xs"
                    />
                  </Chip>
                  <span className="sr-only">Share on via email</span>
                </a>
              </div>
            </div>
          </ResourceSidebar>
          <div className="resource-body col-span-12 md:col-span-8">
            <div className="mb-50">
              {renderRichText(
                data.contentfulResources.bodyText,
                richTextOptions
              )}
            </div>
          </div>
          <div className="col-span-12">
            <ValuePropBullet title="RELATED POSTS" />
          </div>
          {data.allContentfulResources.nodes.map((resource) => (
            <ResourceCard
              title={resource.title}
              category={resource.category.name}
              date={resource.date}
              imageAlt={resource.heroImage.title}
              imageFile={resource.heroImage.gatsbyImageData}
              key={resource.id}
              linkTo={`/resources/${resource.category.slug}/${resource.slug}/`}
              overlayCategory={resource.overlayCategory}
            />
          ))}
        </div>
      </main>
    </Layout>
  )
}

export const query = graphql`
  query ResourcesPage($id: String) {
    contentfulResources(id: { eq: $id }) {
      id
      slug
      title
      category {
        name
        slug
      }
      tags {
        name
        id
        slug
      }
      bodyText {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            title
            gatsbyImageData(width: 1000)
            __typename
          }
          ... on ContentfulHubspotFormEmbed {
            contentful_id
            name
            __typename
            formId
            sys {
              contentType {
                sys {
                  id
                }
              }
            }
          }
          ... on ContentfulTables {
            contentful_id
            name
            __typename
            table {
              childMarkdownRemark {
                html
              }
            }
          }
          ... on ContentfulYouTubeEmbedLink {
            contentful_id
            name
            __typename
            embedId
          }
          ... on ContentfulEmbedCode {
            contentful_id
            name
            __typename
            code {
              code
            }
          }
        }
      }
      overlayCategory
      heroImage {
        gatsbyImageData(layout: FULL_WIDTH)
        title
        file {
          url
        }
      }
      date(formatString: "D MMMM YYYY")
      featured
    }
    allContentfulResources(
      sort: { date: DESC }
      filter: { category: { resources: { elemMatch: { id: { eq: $id } } } } }
      limit: 3
    ) {
      nodes {
        title
        slug
        category {
          name
          slug
        }
        date(formatString: "D MMMM YYYY")
        heroImage {
          gatsbyImageData(layout: FULL_WIDTH)
          title
        }
        overlayCategory
        id
      }
    }
    allContentfulResourceCategories(sort: { order: ASC }) {
      nodes {
        name
        slug
      }
    }
    sidebarImage: contentfulAsset(
      contentful_id: { eq: "J8lLsSSWTUDpN3n7TLrUl" }
    ) {
      title
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
`

export default ResourcesCategory

export const Head = ({ data, location }: HeadProps<SeoProps>) => (
  <SEO
    title={`Resources - ${data.contentfulResources.title}`}
    description="Read the latest market intelligence in consumer credit, company news, and product updates at dv01."
    pathname={location.pathname}
    image={data.contentfulResources.heroImage.file.url}
  />
)
